import { useState } from "react";
import { Botao, Falha, Girando, Selo, cx } from "../../components/ui.js";
import { descreverTrabalho, organizarFila, type TrabalhoDaFila } from "./fila.js";
import { mensagemDoErro } from "./impressora.js";
import {
  useAgora,
  useAnuncioDaFila,
  useCancelarTrabalho,
  useTrabalhosDeImpressao,
} from "./useFilaDeImpressao.js";

/**
 * Um trabalho da fila: o que foi pedido, em que ponto está e o que fazer.
 *
 * O que terminou fica mais quieto (sem borda cheia, texto fraco): é histórico, e o que o operador
 * acompanha é o que ainda está aberto. A exceção é o que **falhou** — o motivo e o que fazer continuam
 * com a cor do texto, porque é o único terminado que pede uma ação.
 */
export function CartaoDoTrabalho({
  trabalho,
  agora,
  cancelando,
  erro,
  aoCancelar,
}: {
  trabalho: TrabalhoDaFila;
  agora: Date;
  cancelando: boolean;
  erro: string | null;
  aoCancelar: (id: number) => void;
}) {
  const linha = descreverTrabalho(trabalho, agora);
  const falhou = trabalho.estado === "falhou";

  return (
    <li
      className={cx(
        "flex flex-col rounded-peca border px-3 py-3",
        linha.terminado ? "border-borda/60" : "border-borda bg-superficie",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <Selo tom={linha.selo.tom}>
          {trabalho.estado === "imprimindo" && <Girando className="size-3" />}
          {linha.selo.rotulo}
        </Selo>
        {linha.progresso && (
          <span className="num text-[0.8125rem] font-medium text-texto">{linha.progresso}</span>
        )}
        <span className="ml-auto text-[0.75rem] text-texto-suave">{linha.quando}</span>
      </div>

      <p className={cx("mt-1.5 text-[0.875rem] wrap-anywhere", "text-texto-suave")}>
        {linha.titulo}
      </p>

      {linha.detalhe && (
        <p
          className={cx(
            "mt-1.5 text-[0.8125rem] leading-relaxed wrap-anywhere",
            linha.terminado && !falhou ? "text-texto-suave" : "text-texto",
          )}
        >
          {linha.detalhe}
        </p>
      )}
      {linha.dica && (
        <p className="mt-1.5 text-[0.8125rem] leading-relaxed text-brasa">{linha.dica}</p>
      )}

      {linha.podeCancelar && (
        <div className="mt-2.5 flex flex-col items-start gap-1.5">
          <Botao
            tom="descartar"
            className="min-h-11"
            carregando={cancelando}
            onClick={() => aoCancelar(trabalho.id)}
          >
            Cancelar
          </Botao>
          {linha.avisoDoCancelar && (
            <p className="text-[0.75rem] leading-relaxed text-texto-suave">
              {linha.avisoDoCancelar}
            </p>
          )}
        </div>
      )}
      {erro && (
        <p role="alert" className="mt-2 text-[0.8125rem] leading-relaxed text-descartar">
          {erro}
        </p>
      )}
    </li>
  );
}

/**
 * A fila de impressão da página: o que está esperando ou imprimindo, e os últimos que terminaram.
 *
 * Sem trabalho nenhum a seção some — uma casa que nunca imprimiu por aqui não precisa de um bloco vazio
 * dizendo "fila". A região `aria-live` fica sempre montada, mesmo sem seção: um leitor de tela só
 * anuncia o que muda dentro de uma região que já existia.
 */
export function FilaDeImpressao() {
  const trabalhos = useTrabalhosDeImpressao();
  const agora = useAgora();
  const { cancelar, cancelandoId, erro } = useCancelarTrabalho();
  const [verTodos, setVerTodos] = useState(false);
  const anuncio = useAnuncioDaFila(trabalhos.data);

  const dados = trabalhos.data ?? [];
  const fila = organizarFila(dados);
  const aVista = [...fila.abertos, ...fila.recentes];

  const lista = (itens: TrabalhoDaFila[]) =>
    itens.map((t) => (
      <CartaoDoTrabalho
        key={t.id}
        trabalho={t}
        agora={agora}
        cancelando={cancelandoId === t.id}
        erro={erro?.id === t.id ? erro.mensagem : null}
        aoCancelar={cancelar}
      />
    ));

  return (
    <>
      <p role="status" aria-live="polite" className="sr-only">
        {anuncio}
      </p>

      {trabalhos.isError && !trabalhos.data && (
        <Falha
          mensagem={mensagemDoErro(trabalhos.error, "Não foi possível ver a fila de impressão.")}
          tentarNovamente={() => void trabalhos.refetch()}
        />
      )}

      {dados.length > 0 && (
        <section
          aria-labelledby="titulo-da-fila"
          className="flex flex-col gap-2 border-t border-borda pt-4"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="titulo-da-fila" className="text-[0.9375rem] font-medium text-texto">
              Fila de impressão
            </h2>
            {fila.abertos.length > 0 && (
              <span className="num text-[0.8125rem] text-texto-suave">
                {fila.abertos.length} em andamento
              </span>
            )}
          </div>

          <ul className="flex flex-col gap-2">{lista(aVista)}</ul>

          {fila.anteriores.length > 0 && (
            <>
              <Botao
                tom="fantasma"
                formato="nu"
                className="min-h-11 underline underline-offset-4"
                aria-expanded={verTodos}
                onClick={() => setVerTodos((v) => !v)}
              >
                {verTodos ? "Esconder os anteriores" : `Ver anteriores (${fila.anteriores.length})`}
              </Botao>
              {verTodos && <ul className="flex flex-col gap-2">{lista(fila.anteriores)}</ul>}
            </>
          )}
        </section>
      )}
    </>
  );
}
